import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Clock, Download, ExternalLink, Film, Info, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchEventWatch,
  fetchVideoPlayback,
  fetchVideoDownload,
  formatMoney,
  friendlyError,
  startContentDownloadCheckout,
} from '@/lib/api';
import { VideoPlayer } from '@/components/videos/VideoPlayer';
import { EventPaywall } from '@/components/events/EventPaywall';
import { ContentPaywall } from '@/components/videos/ContentPaywall';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_LABELS = {
  published: 'Disponible',
  replay_available: 'Replay disponible',
  processing: 'Procesando',
  unavailable: 'No disponible',
  draft: 'Borrador',
  private: 'Privado',
  archived: 'Archivado',
};

const STATUS_MSG = {
  processing: 'El contenido se está procesando. Vuelve en unos minutos.',
  unavailable: 'Contenido no disponible en este momento.',
  archived: 'Este contenido ha sido archivado.',
};

function VideoUnavailable({ status, message }) {
  const text = message || STATUS_MSG[status] || 'Contenido no disponible.';
  return (
    <div
      data-testid="video-unavailable"
      className="w-full aspect-video bg-[var(--ns-bg-2)] rounded-2xl border border-dashed border-[var(--ns-border)] flex items-center justify-center"
    >
      <div className="text-center max-w-md p-6">
        <Clock className="h-10 w-10 text-[var(--ns-gold-700)] mx-auto mb-3" />
        <h3 className="font-display text-lg text-[var(--ns-text)] mb-1">
          {STATUS_LABELS[status] || 'No disponible'}
        </h3>
        <p className="text-sm text-[var(--ns-text-muted)]">{text}</p>
      </div>
    </div>
  );
}

function WatchDownloadButton({ video, eventSlug }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const d = video?.download;
  if (!d || video.download_mode === 'none') return null;

  // Si requiere login y NO hay user → no mostrar (el botón ver con Premium / desbloquear ya cubre el caso)
  if (d.state === 'requires_login') return null;
  // Si el usuario aún no tiene acceso de reproducción, ocultamos la descarga (regla 4)
  if (d.state === 'must_buy_replay_first') return null;
  if (d.state === 'disabled') return null;

  const handleDownload = async () => {
    setBusy(true);
    try {
      const data = await fetchVideoDownload(video.id);
      if (data?.download_url) {
        const a = document.createElement('a');
        a.href = data.download_url;
        a.download = data.filename || 'contenido.mp4';
        a.target = '_blank'; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove();
        toast.success('Descarga iniciada.');
      }
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setBusy(false); }
  };

  const handleBuy = async () => {
    // Tras comprar la descarga volvemos a la ficha del partido anclados
    // al bloque de contenidos (compra=descarga permite a EventDetail
    // mostrar el estado de confirmación de compra).
    const downloadNext = `/eventos/${eventSlug}?compra=descarga#contenidos`;
    const nextEnc = encodeURIComponent(downloadNext);
    if (!user) {
      navigate(`/acceder?next=${nextEnc}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await startContentDownloadCheckout(video.id);
      navigate(`/checkout/sesion/${data.id}?next=${nextEnc}`);
    } catch (err) { toast.error(friendlyError(err)); }
    finally { setBusy(false); }
  };

  // Disponible → Descargar directamente
  if (d.available) {
    return (
      <Button
        onClick={handleDownload} disabled={busy} variant="outline"
        className="mt-4 h-10 rounded-xl border-[var(--ns-border)] text-[var(--ns-text)]"
        data-testid="watch-download-btn"
      >
        <Download className="h-4 w-4 mr-2" />
        {busy ? 'Procesando…' : 'Descargar'}
      </Button>
    );
  }
  // must_buy → ofrecer comprar la descarga
  if (d.state === 'must_buy') {
    return (
      <Button
        onClick={handleBuy} disabled={busy} variant="outline"
        className="mt-4 h-10 rounded-xl border-[var(--ns-border)] text-[var(--ns-text)]"
        data-testid="watch-download-buy-btn"
      >
        <Download className="h-4 w-4 mr-2" />
        {busy ? 'Procesando…' : `Descargar por ${formatMoney(d.price_cents, d.currency)}`}
      </Button>
    );
  }
  return null;
}

export default function WatchPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const requestedVideoId = searchParams.get('v');
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [playback, setPlayback] = useState(null);
  const [playbackError, setPlaybackError] = useState(null);
  const [activeVideoId, setActiveVideoId] = useState(null);

  // Guard de autenticación — Consumir contenido (incl. gratuito) requiere
  // sesión. Si el backend devolviera `access=true` para anónimos (p. ej.
  // contenido gratuito), seguiríamos entrando directamente al player. Para
  // evitarlo redirigimos a /acceder con `?next=` seguro apuntando a esta
  // misma ruta. El retorno post-login lo gestiona Login.jsx vía
  // `sanitizeInternalNext` ya existente.
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    const nextPath = `${location.pathname}${location.search || ''}`;
    navigate(`/acceder?next=${encodeURIComponent(nextPath)}`, { replace: true });
  }, [authLoading, user, location.pathname, location.search, navigate]);

  // Cargar watch data
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await fetchEventWatch(slug);
        if (cancelled) return;
        setData(d);
        // Preselección: si la URL trae ?v=<id> y ese contenido existe en la
        // lista devuelta, se abre ese contenido concreto. En caso contrario
        // cae al `selected_video_id` por defecto del backend.
        const requestedExists = requestedVideoId
          && (d?.videos || []).some((v) => v.id === requestedVideoId);
        setActiveVideoId(requestedExists ? requestedVideoId : (d?.selected_video_id || null));
        setError(null);
      } catch (err) {
        if (!cancelled) setError(friendlyError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, user?.id, requestedVideoId]);

  // Cargar playback cuando hay vídeo activo Y access concedido
  useEffect(() => {
    let cancelled = false;
    setPlayback(null);
    setPlaybackError(null);
    if (!activeVideoId || !data) return;
    const active = (data.videos || []).find((v) => v.id === activeVideoId);
    if (!active) return;
    const isPlayable = ['published', 'replay_available'].includes(active.status);
    if (!isPlayable) return;
    // Acceso por vídeo (preferido) con fallback al `selected_access` legacy.
    const access = active.access
      || (activeVideoId === data.selected_video_id ? data.selected_access : null);
    if (!access?.access) return;

    (async () => {
      try {
        const p = await fetchVideoPlayback(activeVideoId);
        if (!cancelled) setPlayback(p);
      } catch (err) {
        if (!cancelled) setPlaybackError(friendlyError(err));
      }
    })();
    return () => { cancelled = true; };
  }, [activeVideoId, data]);

  const activeVideo = useMemo(() => {
    if (!data?.videos) return null;
    return data.videos.find((v) => v.id === activeVideoId) || null;
  }, [data, activeVideoId]);

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="aspect-video bg-[var(--ns-bg-2)] rounded-2xl animate-pulse" />
        </div>
      </MainLayout>
    );
  }

  if (error || !data) {
    return (
      <MainLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card className="p-8 text-center" data-testid="watch-error">
            <Info className="h-10 w-10 text-[var(--ns-gold-700)] mx-auto mb-3" />
            <h2 className="font-display text-xl text-[var(--ns-text)]">No se ha podido cargar este partido</h2>
            <p className="text-sm text-[var(--ns-text-muted)] mt-2">{error || 'Evento no encontrado.'}</p>
            <Link to="/eventos">
              <Button className="mt-5" data-testid="watch-back-events">Ver eventos</Button>
            </Link>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const ev = data.event;
  const hasVideos = (data.videos || []).length > 0;
  // Acceso del vídeo actualmente seleccionado (preferimos el campo per-video).
  const access = activeVideo?.access
    || (activeVideoId === data.selected_video_id ? data.selected_access : null);
  const accessDenied = Boolean(access && !access.access);
  const requiresLogin = accessDenied && access?.reason === 'unauthenticated';

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="watch-page">
        <Link
          to={`/eventos/${ev.slug}`}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--ns-text-muted)] hover:text-[var(--ns-gold-700)] transition-colors mb-4"
          data-testid="watch-back-link"
        >
          <ArrowLeft className="h-4 w-4" /> Volver al partido
        </Link>

        <header className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold">
            Reproducción
          </p>
          <h1 className="mt-2 font-display text-2xl sm:text-3xl text-[var(--ns-text)] leading-tight">
            {ev.title}
          </h1>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 space-y-4">
            {!hasVideos ? (
              <VideoUnavailable status="unavailable" message="Este partido aún no tiene contenidos publicados." />
            ) : !activeVideo ? (
              <VideoUnavailable status="unavailable" />
            ) : !['published', 'replay_available'].includes(activeVideo.status) ? (
              <VideoUnavailable status={activeVideo.status} />
            ) : accessDenied ? (
              <div data-testid="watch-paywall">
                {(activeVideo?.access_type === 'pay_per_content' || activeVideo?.access_type === 'premium_or_pay_per_content') ? (
                  <ContentPaywall
                    content={{
                      id: activeVideo.id,
                      title: activeVideo.title,
                      price_cents: activeVideo.price_cents,
                      currency: activeVideo.currency || 'EUR',
                    }}
                    eventSlug={ev.slug}
                    requiresLogin={requiresLogin}
                  />
                ) : (
                  <EventPaywall
                    event={{
                      id: ev.id, slug: ev.slug, title: ev.title,
                      access_type: ev.access_type, club_id: ev.club_id,
                      price_cents: null, currency: 'EUR',
                    }}
                    requires={requiresLogin ? 'subscription_or_purchase' : (access?.reason === 'no_entitlement' ? 'subscription_or_purchase' : 'purchase')}
                  />
                )}
              </div>
            ) : playbackError ? (
              <div
                className="w-full aspect-video bg-[var(--ns-bg-2)] rounded-2xl border border-dashed border-[var(--ns-border)] flex items-center justify-center"
                data-testid="watch-playback-error"
              >
                <div className="text-center max-w-md p-6">
                  <Info className="h-10 w-10 text-red-500 mx-auto mb-3" />
                  <h3 className="font-display text-lg mb-1">Error obteniendo la reproducción</h3>
                  <p className="text-sm text-[var(--ns-text-muted)]">{playbackError}</p>
                </div>
              </div>
            ) : !playback ? (
              <div className="w-full aspect-video bg-black/90 rounded-2xl flex items-center justify-center">
                <PlayCircle className="h-16 w-16 text-white/30" />
              </div>
            ) : (
              // Fase Contenidos v2 · Enlace web externo: tras aplicar el
              // access check habitual (Gratis/Premium/PPV) el backend devuelve
              // protocol="external_link". Mostramos un botón "Acceder al
              // contenido" que abre la URL en una nueva pestaña. NO se usa
              // el reproductor de vídeo para este tipo.
              // Para content_kind="statistics" el CTA se renombra a
              // "Ver estadísticas" reutilizando exactamente la misma lógica.
              playback.protocol === 'external_link' ? (
                (() => {
                  const isStats = activeVideo?.content_kind === 'statistics';
                  return (
                <div className="w-full aspect-video bg-[var(--ns-bg-2)] rounded-2xl flex items-center justify-center border border-[var(--ns-border)]">
                  <div className="text-center max-w-md p-6">
                    <ExternalLink className="h-10 w-10 text-[var(--ns-gold-700)] mx-auto mb-3" />
                    <h3 className="font-display text-lg mb-1">
                      {isStats ? 'Estadísticas del partido' : 'Contenido externo'}
                    </h3>
                    <p className="text-sm text-[var(--ns-text-muted)] mb-4">
                      {isStats
                        ? 'Las estadísticas de este partido están alojadas fuera de NetScouters. Se abrirán en una nueva pestaña.'
                        : 'Este contenido está alojado fuera de NetScouters. Se abrirá en una nueva pestaña.'}
                    </p>
                    <Button
                      asChild
                      data-testid={isStats ? 'watch-external-link-stats' : 'watch-external-link-open'}
                      className="h-11 px-5 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0 gap-2"
                    >
                      <a href={playback.external_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" /> {isStats ? 'Ver estadísticas' : 'Acceder al contenido'}
                      </a>
                    </Button>
                  </div>
                </div>
                  );
                })()
              ) : (
                <VideoPlayer
                  protocol={playback.protocol}
                  playbackUrl={playback.playback_url}
                  embedUrl={playback.embed_url}
                  poster={activeVideo.thumbnail_url}
                  onError={() => { /* el componente ya muestra el error en pantalla */ }}
                  testId="watch-player"
                />
              )
            )}

            {activeVideo ? (
              <div className="px-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text)] border-[var(--ns-border)] uppercase text-[10px] tracking-wider">
                    {activeVideo.video_type}
                  </Badge>
                  <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border-[var(--ns-border)] text-[10px]">
                    {STATUS_LABELS[activeVideo.status] || activeVideo.status}
                  </Badge>
                  {activeVideo.is_seed ? (
                    <Badge className="bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)] border-0 text-[10px]">
                      DEMO
                    </Badge>
                  ) : null}
                </div>
                <h2 className="font-display text-xl text-[var(--ns-text)]">{activeVideo.title}</h2>
                {activeVideo.description ? (
                  <p className="text-sm text-[var(--ns-text-muted)] mt-1">{activeVideo.description}</p>
                ) : null}
                {/* Botón Descargar (Entrega 3) */}
                <WatchDownloadButton video={activeVideo} eventSlug={ev.slug} />
              </div>
            ) : null}
          </section>

          <aside className="space-y-3">
            <h3 className="text-[11px] uppercase tracking-[0.24em] text-[var(--ns-gold-700)] font-semibold flex items-center gap-1.5">
              <Film className="h-3.5 w-3.5" /> Más contenidos del partido
            </h3>
            {hasVideos ? (
              <div className="space-y-2" data-testid="watch-video-list">
                {data.videos.map((v) => {
                  const active = v.id === activeVideoId;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setActiveVideoId(v.id)}
                      data-testid={`watch-video-item-${v.id}`}
                      className={[
                        'w-full text-left rounded-xl border p-3 transition-colors',
                        active
                          ? 'border-[var(--ns-gold)]/60 bg-[var(--ns-gold)]/10'
                          : 'border-[var(--ns-border)] hover:bg-[var(--ns-bg-2)]',
                      ].join(' ')}
                    >
                      <div className="flex items-start gap-3">
                        {v.thumbnail_url ? (
                          <img
                            src={v.thumbnail_url}
                            alt=""
                            className="w-20 h-12 rounded-md object-cover bg-[var(--ns-bg-2)] shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-20 h-12 rounded-md bg-[var(--ns-bg-2)] shrink-0 flex items-center justify-center">
                            <PlayCircle className="h-5 w-5 text-[var(--ns-text-muted)]" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[var(--ns-text)] font-medium line-clamp-2">{v.title}</p>
                          <p className="text-[11px] text-[var(--ns-text-subtle)] uppercase tracking-wider mt-0.5">
                            {v.video_type} · {STATUS_LABELS[v.status] || v.status}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-[var(--ns-text-muted)]">Sin contenidos asociados a este partido.</p>
            )}
          </aside>
        </div>
      </div>
    </MainLayout>
  );
}
